const initialState = {
  isLoading: false,
  data: [],
  catalog: {
    items: [],
    isLoading: false,
    error: null,
  },
  // Per-fax-line auto-upload configuration. `lines` are the fax lines the caller
  // may see, `categories` carry each category's mapped line ids. `loaded` is kept
  // separate from `isLoading` so the page can tell "not fetched yet" from "empty".
  autoUpload: {
    lines: [],
    categories: [],
    canEdit: false,
    orgWideAutoUpload: false,
    isLoading: false,
    loaded: false,
    error: null,
  },
  error: false,
  errorMessage: null,
  headers: null,
  errorCode: '',
};

export default (state = initialState, action) => {
  switch (action.type) {
    case 'BEGIN_GET_CATEGORIES': {
      return {
        ...state,
        isLoading: true,
        error: false,
      };
    }
    case 'FAILED_GET_CATEGORIES': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: action.payload.data.message,
      };
    }
    case 'FINISH_GET_CATEGORIES': {
      return {
        ...state,
        isLoading: false,
        error: false,
        data: action.payload.data.categories,
        aiTag: action.payload.data.categories.ai_tag,
        headers: action.payload.data.meta,
        errorMessage: null,
        errorCode: '',
      };
    }
    case 'BEGIN_CREATE_CATEGORY': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
        errorCode: '',
      };
    }
    case 'FAILED_CREATE_CATEGORY': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: action.payload.response.data.error,
        errorCode: action.payload.response.status,
      };
    }
    case 'FINISH_CREATE_CATEGORY': {
      return {
        ...state,
        isLoading: false,
        error: false,
        errorMessage: null,
        errorCode: '',
      };
    }
    case 'BEGIN_DELETE_CATEGORY': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
        errorCode: '',
      };
    }
    case 'FAILED_DELETE_CATEGORY': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: action.payload.response.data.error,
        errorCode: action.payload.response.status,
      };
    }
    case 'FINISH_DELETE_CATEGORY': {
      return {
        ...state,
        isLoading: false,
        error: false,
        errorMessage: null,
        errorCode: '',
      };
    }
    case 'BEGIN_UPDATE_CATEGORY': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
        errorCode: '',
      };
    }
    case 'FAILED_UPDATE_CATEGORY': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorCode: action.payload.response.status,
      };
    }
    case 'FINISH_UPDATE_CATEGORY': {
      const category = action.payload?.data?.category;
      const organizationState = Object.fromEntries([
        'enabled', 'ever_used', 'emr_category', 'classification_hints',
        'include_insurance_referral_authorizations', 'send_performed_date', 'title_example',
        'title_instruction', 'title_options', 'task_dedup_eligible',
      ].filter((key) => Object.prototype.hasOwnProperty.call(category || {}, key))
        .map((key) => [key, category[key]]));
      return {
        ...state,
        isLoading: false,
        error: false,
        errorMessage: null,
        errorCode: '',
        catalog: {
          ...state.catalog,
          items: state.catalog.items.map((entry) => (
            category?.id != null && entry.organization_state.document_category_id === category.id
              ? { ...entry, organization_state: { ...entry.organization_state, ...organizationState } }
              : entry
          )),
        },
      };
    }
    case 'BEGIN_GET_CATEGORY_CATALOG':
      return { ...state, catalog: { ...state.catalog, isLoading: true, error: null } };

    case 'FAILED_GET_CATEGORY_CATALOG':
      return {
        ...state,
        catalog: {
          ...state.catalog,
          isLoading: false,
          error: action.payload?.data?.message || 'Failed to load catalog',
        },
      };

    case 'FINISH_GET_CATEGORY_CATALOG':
      return {
        ...state,
        catalog: { items: action.payload.data.catalog, isLoading: false, error: null },
      };

    case 'FINISH_SET_CATEGORY_SFTP_ONLY': {
      const { category } = action.payload.data;
      return {
        ...state,
        data: state.data.map((entry) => (entry.id === category.id ? { ...entry, ...category } : entry)),
        catalog: {
          ...state.catalog,
          items: state.catalog.items.map((entry) => (
            entry.organization_state.document_category_id === category.id
              ? { ...entry, organization_state: { ...entry.organization_state, sftp_only: category.sftp_only } }
              : entry
          )),
        },
      };
    }

    case 'FINISH_ENABLE_SUPPORTED_CATEGORY':
    case 'FINISH_DISABLE_SUPPORTED_CATEGORY': {
      const updatedDc = action.payload.data.category;
      const items = state.catalog.items.map((entry) => {
        if (entry.organization_state.document_category_id === updatedDc.id
          || entry.id === updatedDc.ai_supported_document_category_id) {
          return {
            ...entry,
            organization_state: {
              sftp_only: !!updatedDc.sftp_only,
              enabled: updatedDc.enabled,
              ever_used: updatedDc.ever_used,
              document_category_id: updatedDc.id,
              emr_category: updatedDc.emr_category,
              classification_hints: updatedDc.classification_hints || [],
              include_insurance_referral_authorizations: updatedDc.include_insurance_referral_authorizations !== false,
              send_performed_date: !!updatedDc.send_performed_date,
              title_example: updatedDc.title_example || null,
              title_instruction: updatedDc.title_instruction || null,
              task_dedup_eligible: !!updatedDc.task_dedup_eligible,
            },
          };
        }
        return entry;
      });
      return { ...state, catalog: { ...state.catalog, items } };
    }

    case 'FAILED_ENABLE_SUPPORTED_CATEGORY':
    case 'FAILED_DISABLE_SUPPORTED_CATEGORY':
      return {
        ...state,
        catalog: {
          ...state.catalog,
          error: action.payload?.data?.message || 'Request failed',
        },
      };

    case 'FINISH_ADD_CATEGORY_HINT':
    case 'FINISH_REMOVE_CATEGORY_HINT': {
      const updatedDc = action.payload.data.category;
      const items = state.catalog.items.map((entry) => {
        if (entry.organization_state.document_category_id === updatedDc.id) {
          return {
            ...entry,
            organization_state: {
              ...entry.organization_state,
              classification_hints: updatedDc.classification_hints || [],
            },
          };
        }
        return entry;
      });
      return { ...state, catalog: { ...state.catalog, items } };
    }

    case 'FAILED_ADD_CATEGORY_HINT':
    case 'FAILED_REMOVE_CATEGORY_HINT':
      return {
        ...state,
        catalog: {
          ...state.catalog,
          error: action.payload?.response?.data?.error
            || action.payload?.data?.error
            || 'Hint request failed',
        },
      };

    case 'FINISH_SET_CATEGORY_TITLE_INSTRUCTION': {
      const updatedDc = action.payload.data.category;
      const items = state.catalog.items.map((entry) => {
        if (entry.organization_state.document_category_id === updatedDc.id) {
          return {
            ...entry,
            organization_state: {
              ...entry.organization_state,
              title_example: updatedDc.title_example || null,
              title_instruction: updatedDc.title_instruction || null,
            },
          };
        }
        return entry;
      });
      return { ...state, catalog: { ...state.catalog, items } };
    }

    case 'FAILED_SET_CATEGORY_TITLE_INSTRUCTION':
      return {
        ...state,
        catalog: {
          ...state.catalog,
          error: action.payload?.response?.data?.error
            || action.payload?.data?.error
            || 'Custom titling request failed',
        },
      };

    case 'FINISH_SET_CATEGORY_TITLE_OPTIONS': {
      const updatedDc = action.payload.data.category;
      const items = state.catalog.items.map((entry) => {
        if (entry.organization_state.document_category_id === updatedDc.id) {
          return {
            ...entry,
            organization_state: {
              ...entry.organization_state,
              title_options: updatedDc.title_options || [],
            },
          };
        }
        return entry;
      });
      return { ...state, catalog: { ...state.catalog, items } };
    }

    case 'FAILED_SET_CATEGORY_TITLE_OPTIONS':
      return {
        ...state,
        catalog: {
          ...state.catalog,
          error: action.payload?.response?.data?.error
            || action.payload?.data?.error
            || 'Title options request failed',
        },
      };

    case 'BEGIN_GET_AUTO_UPLOAD_CATEGORIES':
      return { ...state, autoUpload: { ...state.autoUpload, isLoading: true, error: null } };

    case 'FINISH_GET_AUTO_UPLOAD_CATEGORIES': {
      const payload = action.payload.data;
      return {
        ...state,
        autoUpload: {
          lines: payload.fax_numbers || [],
          categories: payload.categories || [],
          canEdit: !!payload.can_edit,
          orgWideAutoUpload: !!payload.org_wide_auto_upload,
          isLoading: false,
          loaded: true,
          error: null,
        },
      };
    }

    case 'FAILED_GET_AUTO_UPLOAD_CATEGORIES':
      return {
        ...state,
        autoUpload: {
          ...state.autoUpload,
          isLoading: false,
          // `loaded` stays true on a refetch failure so an error doesn't blank out
          // configuration the user is already looking at.
          error: action.payload?.response?.data?.error
            || action.payload?.data?.error
            || 'Failed to load auto-upload settings',
        },
      };

    // The response carries the saved category, so the row is replaced from the
    // server's version rather than from the draft the drawer sent — the two can
    // differ when a line was rejected or a mapping outside the caller's scope was
    // preserved.
    case 'FINISH_UPDATE_AUTO_UPLOAD_CATEGORY': {
      const saved = action.payload.data.category;
      if (!saved) return state;

      const categories = state.autoUpload.categories.map((category) => (
        category.document_category_id === saved.document_category_id ? saved : category
      ));
      return { ...state, autoUpload: { ...state.autoUpload, categories, error: null } };
    }

    case 'FAILED_UPDATE_AUTO_UPLOAD_CATEGORY':
      return {
        ...state,
        autoUpload: {
          ...state.autoUpload,
          error: action.payload?.response?.data?.error
            || action.payload?.data?.error
            || 'Failed to save auto-upload settings',
        },
      };

    default:
      return state;
  }
};
